/**
 * `PopoverMenu` — portal-rendered menu / popover anchored to a trigger.
 *
 * Why portal: an inline `position: absolute` dropdown inherits any
 * ancestor `overflow: hidden` / stacking context, so row menus on
 * tables inside bordered cards clip behind the next row. Rendering to
 * `document.body` lifts the menu out of every ancestor transform / clip.
 *
 * Behaviour:
 *   - Positioned by the shared `useAnchoredPosition` hook (same as
 *     `Tooltip` / `Popover`): flips above when there is no room below,
 *     aligns to the trigger's right edge by default (table row context)
 *     and shifts to stay at least 8px inside the viewport.
 *   - Closes on outside click (any mouse down outside the trigger or
 *     the menu), Escape, scroll (window or ancestor scroll — fires on
 *     capture so nested scrollables close the menu), or route change
 *     (the next `open` state transitioning to false).
 *   - Keyboard-accessible: arrow Up/Down moves focus between items,
 *     Enter / Space activates, Tab closes.
 *
 * Not a general portal/popper — a focused menu primitive sized for
 * the dashboard's 10–200 px dropdowns, no arrows, no sub-menus.
 */

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useC } from '../../lib/theme.js';
import { useAnchoredPosition } from './useAnchoredPosition.js';

export interface PopoverMenuProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly anchorRef: React.RefObject<HTMLElement>;
  readonly children: React.ReactNode;
  /** Preferred horizontal alignment relative to the trigger. */
  readonly align?: 'start' | 'end';
  /** Preferred vertical placement; the component flips if room is tight. */
  readonly placement?: 'bottom' | 'top';
  /** CSS min-width (also the default width if children don't force it). */
  readonly minWidth?: number;
  /** data-testid for the menu container (not the trigger). */
  readonly testid?: string;
  /** Optional aria-label; defaults to generic "Actions". */
  readonly ariaLabel?: string;
}

const GAP = 4;

export function PopoverMenu({
  open,
  onClose,
  anchorRef,
  children,
  align = 'end',
  placement = 'bottom',
  minWidth = 180,
  testid,
  ariaLabel = 'Actions',
}: PopoverMenuProps): JSX.Element | null {
  const C = useC();
  const menuRef = useRef<HTMLDivElement | null>(null);
  // Shared anchored positioning (flip + shift, ≥8px inside the viewport):
  // the same hook as `Tooltip` and `Popover` (labels-review/ux.md §12.3).
  const pos = useAnchoredPosition(anchorRef, menuRef, { open, side: placement, offset: GAP, align });

  // Any scroll closes the menu (capture phase, so nested scroll containers
  // count) rather than letting it drift away from its row.
  useEffect(() => {
    if (!open) return;
    const onScroll = (e: Event): void => {
      if (e.target instanceof Node && menuRef.current?.contains(e.target) === true) return;
      onClose();
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (menuRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        anchorRef.current?.focus();
        return;
      }
      if (e.key === 'Tab') {
        onClose();
        return;
      }
      if (!menuRef.current) return;
      const items = Array.from(
        menuRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])'),
      );
      if (items.length === 0) return;
      const current = document.activeElement as HTMLElement | null;
      const idx = current ? items.indexOf(current) : -1;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        items[(idx + 1 + items.length) % items.length]?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        items[(idx - 1 + items.length) % items.length]?.focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        items[0]?.focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        items[items.length - 1]?.focus();
      }
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, anchorRef, onClose]);

  // Focus the first item on open for keyboard users.
  useEffect(() => {
    if (!open || !pos.visible) return;
    const t = window.setTimeout(() => {
      const first = menuRef.current?.querySelector<HTMLElement>(
        '[role="menuitem"]:not([disabled])',
      );
      first?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open, pos.visible]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label={ariaLabel}
      data-testid={testid}
      data-placement={pos.side}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        minWidth,
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        padding: 4,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
        // Popover token: above sticky headers and Modal (z-100), below tooltips.
        zIndex: 'var(--z-popover, 900)' as unknown as number,
        visibility: pos.visible ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

export interface PopoverMenuItemProps {
  readonly label: string;
  readonly onClick: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly icon?: React.ComponentType<any>;
  readonly disabled?: boolean;
  readonly testid?: string;
  readonly tone?: 'default' | 'danger';
}

export function PopoverMenuItem({
  label,
  onClick,
  icon: Icon,
  disabled = false,
  testid,
  tone = 'default',
}: PopoverMenuItemProps): JSX.Element {
  const C = useC();
  const color = tone === 'danger' ? C.coralFg : C.text;
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      data-testid={testid}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '6px 10px',
        background: 'transparent',
        border: 'none',
        fontFamily: "'Geist', sans-serif",
        fontSize: 12,
        color,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        textAlign: 'left',
        borderRadius: 2,
      }}
      onMouseEnter={(e) => {
        if (!disabled) (e.currentTarget as HTMLElement).style.background = C.surface2;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
      onFocus={(e) => {
        if (!disabled) (e.currentTarget as HTMLElement).style.background = C.surface2;
      }}
      onBlur={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
    >
      {Icon ? <Icon size={12} aria-hidden /> : null}
      <span>{label}</span>
    </button>
  );
}
